import { forwardRef, useImperativeHandle, useRef } from 'react';
import { TranslationService } from '../../../../../../services/translation.service';
import RadioFormGroup from '../../../../../Shared/RadioFormGroup';
import ValidationInfo from '../../../../../../validation/validation-info';
import TextFieldFormGroup from '../../../../../Shared/TextFieldFormGroup';
import IValidatable, { IServerValidationError } from '../../../../../Shared/validatable.interface';
import { ReplacementTransportMode } from './replacement-transport-mode.enum';
import { ITransportWarrantyActualArrivalSelectorState, ITransportWarrantyActualArrivalSelectorStateValue } from '../..';
import { TransportWarrantyActualArrivalSelectorField } from '../../transport-warranty-actual-arrival-selector.enum';
import ValidationEngine from '../../../../../../validation/validation-engine';
import NumberFieldFormGroup from '../../../../../Shared/NumberFieldFormGroup';

interface IReplacementTransportProps {
  state: ITransportWarrantyActualArrivalSelectorState;
  onChange?: ((value: ITransportWarrantyActualArrivalSelectorStateValue) => void) | undefined;
  onKeyUp?: ((field: TransportWarrantyActualArrivalSelectorField) => void) | undefined;
}

export default forwardRef<IValidatable, IReplacementTransportProps>(function ReplacementTransport({ state, onChange, onKeyUp }: IReplacementTransportProps, ref) {
  // === REFS ===
  const fromRef = useRef<IValidatable>(null!);
  const toRef = useRef<IValidatable>(null!);
  const taxiSumRef = useRef<IValidatable>(null!);
  const distanceRef = useRef<IValidatable>(null!);
  const registrationNumberRef = useRef<IValidatable>(null!);
  const parkingFeeRef = useRef<IValidatable>(null!);
  const congestionTaxRef = useRef<IValidatable>(null!);
  const receiptSumRef = useRef<IValidatable>(null!);
  const otherTransportTypeRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const transportModeValidationInfo = new ValidationInfo(`${TranslationService.get('delaycompensatiotransportwarrantyformpage.transportmodetext')} - `);
      ValidationEngine.for(transportModeValidationInfo).notEqual(ReplacementTransportMode.none, state.value.transportMode, 'common.validation.requiredfield').validate();
      if (transportModeValidationInfo.isInvalid) {
        return new ValidationInfo('', [], {
          transportMode: transportModeValidationInfo,
        });
      }

      switch (state.value.transportMode) {
        case ReplacementTransportMode.taxi:
          return new ValidationInfo('', [], {
            from: fromRef.current.checkValidity(),
            to: toRef.current.checkValidity(),
            taxiSum: taxiSumRef.current.checkValidity(),
          });
        case ReplacementTransportMode.ownCar:
          return new ValidationInfo('', [], {
            from: fromRef.current.checkValidity(),
            to: toRef.current.checkValidity(),
            registrationNumber: registrationNumberRef.current.checkValidity(),
            distance: distanceRef.current.checkValidity(),
            parkingFee: parkingFeeRef.current.checkValidity(),
            congestionTax: congestionTaxRef.current.checkValidity(),
          });
        case ReplacementTransportMode.other:
          return new ValidationInfo('', [], {
            otherTransportType: otherTransportTypeRef.current.checkValidity(),
            receiptSum: receiptSumRef.current.checkValidity(),
          });
        default:
          return new ValidationInfo('', [], {});
      }
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      switch (state.value.transportMode) {
        case ReplacementTransportMode.taxi:
          return new ValidationInfo('', [], {
            from: fromRef.current.handleServerValidationErrors(errors),
            to: toRef.current.handleServerValidationErrors(errors),
            taxiSum: taxiSumRef.current.handleServerValidationErrors(errors),
          });
        case ReplacementTransportMode.ownCar:
          return new ValidationInfo('', [], {
            from: fromRef.current.handleServerValidationErrors(errors),
            to: toRef.current.handleServerValidationErrors(errors),
            registrationNumber: registrationNumberRef.current.handleServerValidationErrors(errors),
            distance: distanceRef.current.handleServerValidationErrors(errors),
            parkingFee: parkingFeeRef.current.handleServerValidationErrors(errors),
            congestionTax: congestionTaxRef.current.handleServerValidationErrors(errors),
          });
        case ReplacementTransportMode.other:
          return new ValidationInfo('', [], {
            otherTransportType: otherTransportTypeRef.current.handleServerValidationErrors(errors),
            receiptSum: receiptSumRef.current.handleServerValidationErrors(errors),
          });
        default:
          return new ValidationInfo('', [], {});
      }
    },
  }));

  return (
    <div className="mb-3">
      <RadioFormGroup
        id="replacement-transport-mode"
        className="mb-3"
        labelText={`${TranslationService.get('delaycompensatiotransportwarrantyformpage.transportmodetext')}*`}
        options={[
          { value: ReplacementTransportMode.taxi, text: TranslationService.get('delaycompensatiotransportwarrantyformpage.transportmodetaxi') },
          { value: ReplacementTransportMode.ownCar, text: TranslationService.get('delaycompensatiotransportwarrantyformpage.transportmodeowncar') },
          { value: ReplacementTransportMode.other, text: TranslationService.get('delaycompensatiotransportwarrantyformpage.transportmodeother') },
        ]}
        rowLayout={true}
        rowLayoutSmUp={false}
        state={{
          value: state.value.transportMode,
          validationInfo: state.validationInfo.children.transportMode ?? new ValidationInfo(),
        }}
        onChange={(value) => {
          onChange?.({ ...state.value, transportMode: value as ReplacementTransportMode });
          onKeyUp?.(TransportWarrantyActualArrivalSelectorField.transportMode);
        }}
      />
      {state.value.transportMode === ReplacementTransportMode.taxi && (
        <div>
          <TextFieldFormGroup
            ref={fromRef}
            autocomplete="off"
            inputMode="text"
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.from')}
            required={true}
            inputId="from"
            serverValidationName="actualArrivalFrom"
            state={{
              value: state.value.from,
              validationInfo: state.validationInfo.children.from ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, from: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.from);
            }}
          />
          <TextFieldFormGroup
            ref={toRef}
            autocomplete="off"
            inputMode="text"
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.to')}
            required={true}
            inputId="to"
            serverValidationName="actualArrivalTo"
            state={{
              value: state.value.to,
              validationInfo: state.validationInfo.children.to ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, to: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.to);
            }}
          />
          <NumberFieldFormGroup
            ref={taxiSumRef}
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.taxisum')}
            min={1}
            required={true}
            inputId="taxiSum"
            serverValidationName="actualArrivalTaxiSum"
            state={{
              value: state.value.taxiSum,
              validationInfo: state.validationInfo.children.taxiSum ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, taxiSum: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.taxiSum);
            }}
          />
        </div>
      )}
      {state.value.transportMode === ReplacementTransportMode.ownCar && (
        <div>
          <TextFieldFormGroup
            ref={fromRef}
            autocomplete="off"
            inputMode="text"
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.from')}
            required={true}
            inputId="from"
            serverValidationName="actualArrivalFrom"
            state={{
              value: state.value.from,
              validationInfo: state.validationInfo.children.from ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, from: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.from);
            }}
          />
          <TextFieldFormGroup
            ref={toRef}
            autocomplete="off"
            inputMode="text"
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.to')}
            required={true}
            inputId="to"
            serverValidationName="actualArrivalTo"
            state={{
              value: state.value.to,
              validationInfo: state.validationInfo.children.to ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, to: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.to);
            }}
          />
          <div className="d-flex flex-column flex-sm-row flex-sm-nowrap">
            <TextFieldFormGroup
              ref={registrationNumberRef}
              autocomplete="off"
              inputMode="text"
              labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.registrationnumber')}
              required={true}
              inputId="registrationNumber"
              serverValidationName="actualArrivalRegistrationNumber"
              className="w-100 pe-sm-2"
              state={{
                value: state.value.registrationNumber,
                validationInfo: state.validationInfo.children.registrationNumber ?? new ValidationInfo(),
              }}
              onChange={(value) => {
                onChange?.({ ...state.value, registrationNumber: value });
              }}
              onKeyUp={() => {
                onKeyUp?.(TransportWarrantyActualArrivalSelectorField.registrationNumber);
              }}
            />
            <NumberFieldFormGroup
              ref={distanceRef}
              allowDecimals={true}
              labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.roundtripdistance')}
              required={true}
              min={1}
              inputId="distance"
              serverValidationName="actualArrivalDistance"
              className="w-100 ps-sm-2"
              state={{
                value: state.value.distance,
                validationInfo: state.validationInfo.children.distance ?? new ValidationInfo(),
              }}
              onChange={(value) => {
                onChange?.({ ...state.value, distance: value });
              }}
              onKeyUp={() => {
                onKeyUp?.(TransportWarrantyActualArrivalSelectorField.distance);
              }}
            />
          </div>
          <div className="d-flex flex-column flex-sm-row flex-sm-nowrap">
            <NumberFieldFormGroup
              ref={parkingFeeRef}
              allowDecimals={true}
              labelText={`${TranslationService.get('delaycompensatiotransportwarrantyformpage.parkingfee')} (${TranslationService.get(
                'delaycompensatiotransportwarrantyformpage.costsupportedbyreceipt'
              )})`}
              required={false}
              inputId="parkingFee"
              serverValidationName="actualArrivalParkingFee"
              className="w-100 pe-sm-2"
              state={{
                value: state.value.parkingFee,
                validationInfo: state.validationInfo.children.parkingFee ?? new ValidationInfo(),
              }}
              onChange={(value) => {
                onChange?.({ ...state.value, parkingFee: value });
              }}
              onKeyUp={() => {
                onKeyUp?.(TransportWarrantyActualArrivalSelectorField.parkingFee);
              }}
            />
            <NumberFieldFormGroup
              ref={congestionTaxRef}
              allowDecimals={false}
              labelText={`${TranslationService.get('delaycompensatiotransportwarrantyformpage.congestiontax')} (${TranslationService.get(
                'delaycompensatiotransportwarrantyformpage.costsupportedbyreceipt'
              )})`}
              required={false}
              inputId="congestionTax"
              serverValidationName="actualArrivalCongestionTax"
              className="w-100 ps-sm-2"
              state={{
                value: state.value.congestionTax,
                validationInfo: state.validationInfo.children.congestionTax ?? new ValidationInfo(),
              }}
              onChange={(value) => {
                onChange?.({ ...state.value, congestionTax: value });
              }}
              onKeyUp={() => {
                onKeyUp?.(TransportWarrantyActualArrivalSelectorField.congestionTax);
              }}
            />
          </div>
        </div>
      )}
      {state.value.transportMode === ReplacementTransportMode.other && (
        <div>
          <TextFieldFormGroup
            ref={otherTransportTypeRef}
            autocomplete="off"
            inputMode="text"
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.statemeansoftransport')}
            required={true}
            maxLength={500}
            inputId="otherTransportType"
            serverValidationName="actualArrivalOtherTransportType"
            state={{
              value: state.value.otherTransportType,
              validationInfo: state.validationInfo.children.otherTransportType ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, otherTransportType: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.otherTransportType);
            }}
          />
          <NumberFieldFormGroup
            ref={receiptSumRef}
            allowDecimals={true}
            min={1}
            labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.totalamountonreceipt')}
            required={true}
            inputId="receiptSum"
            serverValidationName="actualArrivalReceiptSum"
            state={{
              value: state.value.receiptSum,
              validationInfo: state.validationInfo.children.receiptSum ?? new ValidationInfo(),
            }}
            onChange={(value) => {
              onChange?.({ ...state.value, receiptSum: value });
            }}
            onKeyUp={() => {
              onKeyUp?.(TransportWarrantyActualArrivalSelectorField.receiptSum);
            }}
          />
        </div>
      )}
    </div>
  );
});
